import { Box, Button, Checkbox, FormControlLabel, Grid, MenuItem, Paper, TextField } from "@mui/material";
import { useState } from "react";
import { AutoCompleteClientSelect } from "../../../shared/components/AutoCompleteClientSelect/AutoCompleteClientSelect";
import { EMPTY_SETUP_CHARGE_FILTERS, ISetupChargeFilterState } from "../utils/setupChargeFormat";
import { SETUP_CHARGE_STATUS_OPTIONS } from "../utils/setupChargeStatus";

interface SetupChargeFiltersProps {
	/** Filtros em vigor na tabela; o formulário só os altera ao clicar em "Filtrar". */
	applied: ISetupChargeFilterState;
	onApply: (filters: ISetupChargeFilterState) => void;
	/** Esconde período por/status/duplicidade (usado no relatório, que já fixa as pagas pela data de pagamento). */
	compact?: boolean;
	disabled?: boolean;
}

const GATEWAYS = ["ASAAS"];
const PAYMENT_METHODS = [
	{ value: "PIX", label: "PIX" },
	{ value: "BOLETO", label: "Boleto" },
	{ value: "CREDIT_CARD", label: "Cartão de crédito" },
];

export const SetupChargeFilters = ({ applied, onApply, compact = false, disabled = false }: SetupChargeFiltersProps) => {
	const [draft, setDraft] = useState<ISetupChargeFilterState>(applied);

	const patch = (changes: Partial<ISetupChargeFilterState>) => setDraft((current) => ({ ...current, ...changes }));

	const handleClear = () => {
		setDraft(EMPTY_SETUP_CHARGE_FILTERS);
		onApply(EMPTY_SETUP_CHARGE_FILTERS);
	};

	return (
		<Paper variant="outlined" sx={{ mt: 1, mx: 1, p: 2, width: "96%", borderRadius: 2 }}>
			<form
				onSubmit={(event) => {
					event.preventDefault();
					onApply(draft);
				}}
			>
				<Grid container spacing={2} alignItems="center">
					{!compact && (
						<Grid item xs={12} sm={6} md={3}>
							<TextField select fullWidth size="small" label="Período por" value={draft.dateField} onChange={(e) => patch({ dateField: e.target.value as "created" | "paid" })}>
								<MenuItem value="created">Data de criação</MenuItem>
								<MenuItem value="paid">Data de pagamento</MenuItem>
							</TextField>
						</Grid>
					)}
					<Grid item xs={6} sm={3} md={2}>
						<TextField
							fullWidth
							size="small"
							type="date"
							label="De"
							InputLabelProps={{ shrink: true }}
							value={draft.dateStart}
							onChange={(e) => patch({ dateStart: e.target.value })}
						/>
					</Grid>
					<Grid item xs={6} sm={3} md={2}>
						<TextField
							fullWidth
							size="small"
							type="date"
							label="Até (inclusive)"
							InputLabelProps={{ shrink: true }}
							value={draft.dateEnd}
							onChange={(e) => patch({ dateEnd: e.target.value })}
							inputProps={{ min: draft.dateStart || undefined }}
						/>
					</Grid>
					<Grid item xs={12} sm={6} md={5}>
						<TextField
							fullWidth
							size="small"
							label="Buscar por pagador, documento ou código"
							value={draft.search}
							onChange={(e) => patch({ search: e.target.value })}
						/>
					</Grid>

					<Grid item xs={12} sm={6} md={4}>
						<AutoCompleteClientSelect value={draft.clientId} onChange={(id) => patch({ clientId: id })} labelSelect="Imobiliária" />
					</Grid>
					{!compact && (
						<Grid item xs={12} sm={6} md={3}>
							<TextField
								select
								fullWidth
								size="small"
								label="Status"
								value={draft.status}
								onChange={(e) => patch({ status: typeof e.target.value === "string" ? e.target.value.split(",") : (e.target.value as string[]) })}
								SelectProps={{
									multiple: true,
									renderValue: (selected) => (selected as string[]).map((s) => SETUP_CHARGE_STATUS_OPTIONS.find((o) => o.value === s)?.label ?? s).join(", "),
								}}
							>
								{SETUP_CHARGE_STATUS_OPTIONS.map((option) => (
									<MenuItem key={option.value} value={option.value}>
										<Checkbox size="small" checked={draft.status.includes(option.value)} />
										{option.label}
									</MenuItem>
								))}
							</TextField>
						</Grid>
					)}
					<Grid item xs={6} sm={3} md={2}>
						<TextField select fullWidth size="small" label="Gateway" value={draft.gateway} onChange={(e) => patch({ gateway: e.target.value })}>
							<MenuItem value="">Todos</MenuItem>
							{GATEWAYS.map((gateway) => (
								<MenuItem key={gateway} value={gateway}>
									{gateway}
								</MenuItem>
							))}
						</TextField>
					</Grid>
					<Grid item xs={6} sm={3} md={3}>
						<TextField select fullWidth size="small" label="Forma de pagamento" value={draft.paymentMethod} onChange={(e) => patch({ paymentMethod: e.target.value })}>
							<MenuItem value="">Todas</MenuItem>
							{PAYMENT_METHODS.map((method) => (
								<MenuItem key={method.value} value={method.value}>
									{method.label}
								</MenuItem>
							))}
						</TextField>
					</Grid>

					<Grid item xs={12} md="auto">
						<Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
							{!compact && (
								<FormControlLabel
									control={<Checkbox size="small" checked={draft.duplicatePayment} onChange={(e) => patch({ duplicatePayment: e.target.checked })} />}
									label="Possível duplicidade"
								/>
							)}
							<Button type="submit" variant="contained" disableElevation disabled={disabled}>
								Filtrar
							</Button>
							<Button variant="outlined" onClick={handleClear} disabled={disabled}>
								Limpar
							</Button>
						</Box>
					</Grid>
				</Grid>
			</form>
		</Paper>
	);
};
